import React from 'react';
import { MapPin } from 'lucide-react';
import { useScrollTrigger } from './hooks/useScrollTrigger';

const Clock = ({ size = 24, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`flex-shrink-0 ${className}`}>
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12,6 12,12 16,14"/>
  </svg>
);

const EventDetailsSection = () => {
  const [headerRef, headerVisible] = useScrollTrigger();
  const [venueRef, venueVisible] = useScrollTrigger();
  const [detailsRef, detailsVisible] = useScrollTrigger();
  const [dressCodeRef, dressCodeVisible] = useScrollTrigger();

  return (
    <div className="max-w-5xl mx-auto px-6">
      <div 
        ref={headerRef}
        className={`text-center mb-16 transition-all duration-1000 ${
          headerVisible 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
        }`}
      >
        <h2 className="text-4xl font-serif font-light text-[#7A3E53] mb-4">Our Wedding Day</h2>
        <p className="text-[#5A8E6E] text-lg">The details of our celebration</p>
      </div>

     <div className="grid md:grid-cols-1 gap-10 max-w-4xl mx-auto mb-16">
      <div className="p-10 border border-[#F7C843]/30 rounded-2xl bg-white/80 shadow-sm hover:shadow-lg transition-all duration-300 opacity-0 animate-[fadeInUp_1s_ease-out_0.4s_forwards]">
        <h3 className="text-2xl font-serif font-light text-[#7A3E53] mb-8">Timeline of the Day</h3>
        <div className="space-y-6">
          <div className="flex items-start gap-4 opacity-0 animate-[slideInLeft_0.8s_ease-out_0.6s_forwards]">
            <Clock className="text-[#F36C3D] mt-1 animate-[spin_4s_ease-in-out] w-5 h-5 sm:w-6 sm:h-6" />
            <div>
              <p className="font-serif text-[#7A3E53] mb-1">Arrival</p>
              <p className="text-[#5A8E6E]">3:00pm - 3:15pm</p>
            </div>
          </div>
          <div className="flex items-start gap-4 opacity-0 animate-[slideInLeft_0.8s_ease-out_0.8s_forwards]">
            <Clock className="text-[#F36C3D] mt-1 animate-[spin_4s_ease-in-out] w-5 h-5 sm:w-6 sm:h-6" />
            <div>
              <p className="font-serif text-[#7A3E53] mb-1">Ceremony</p>
              <p className="text-[#5A8E6E]">3:30pm</p>
            </div>
          </div>
          <div className="flex items-start gap-4 opacity-0 animate-[slideInLeft_0.8s_ease-out_1s_forwards]">
            <Clock className="text-[#F36C3D] mt-1 animate-[spin_4s_ease-in-out] w-5 h-5 sm:w-6 sm:h-6" />
            <div>
              <p className="font-serif text-[#7A3E53] mb-1">Family and Friend Portraits</p>
              <p className="text-[#5A8E6E]">4:00pm</p>
            </div>
          </div>
          <div className="flex items-start gap-4 opacity-0 animate-[slideInLeft_0.8s_ease-out_1.2s_forwards]">
            <Clock className="text-[#F36C3D] mt-1 animate-[spin_4s_ease-in-out] w-5 h-5 sm:w-6 sm:h-6" />
            <div>
              <p className="font-serif text-[#7A3E53] mb-1">Welcome Drinks, Harvest Table & Lawn Games</p>
              <p className="text-[#5A8E6E]">4:00pm onwards</p>
            </div>
          </div>
          <div className="flex items-start gap-4 opacity-0 animate-[slideInLeft_0.8s_ease-out_1.4s_forwards]">
            <Clock className="text-[#F36C3D] mt-1 animate-[spin_4s_ease-in-out] w-5 h-5 sm:w-6 sm:h-6" />
            <div>
              <p className="font-serif text-[#7A3E53] mb-1">Reception</p>
              <p className="text-[#5A8E6E]">6:00pm</p>
            </div>
          </div>
          <div className="flex items-start gap-4 opacity-0 animate-[slideInLeft_0.8s_ease-out_1.6s_forwards]">
            <Clock className="text-[#F36C3D] mt-1 animate-[spin_4s_ease-in-out] w-5 h-5 sm:w-6 sm:h-6" />
            <div>
              <p className="font-serif text-[#7A3E53] mb-1">Dinner</p>
              <p className="text-[#5A8E6E]">7:00pm</p>
            </div>
          </div>
        </div>
      </div>
    </div>

      <div className="grid md:grid-cols-2 gap-10 max-w-4xl mx-auto mb-16">
        <div 
          ref={venueRef}
          className={`p-10 border border-[#E76C9B]/20 rounded-2xl bg-white/80 shadow-sm hover:shadow-lg transition-all duration-500 ${
            venueVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-8'
          }`}
        >
          <h3 className="text-2xl font-serif font-light text-[#7A3E53] mb-8">Venue Location</h3>
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <MapPin className="text-[#E76C9B] mt-1 animate-[bounce_2s_ease-in-out_infinite]" size={22} />
              <div>
                <p className="font-serif text-[#7A3E53] mb-1">Swanlake Gardens</p>
                <p className="text-[#5A8E6E]">
                  Lot 266 Kragga Kamma Rd<br />
                  Theescombe, Gqeberha, 6070
                </p>
              </div>
            </div>
          </div>
        </div>

        <div 
          ref={detailsRef}
          className={`p-10 border border-[#9B4F96]/20 rounded-2xl bg-white/80 shadow-sm hover:shadow-lg transition-all duration-500 ${
            detailsVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: detailsVisible ? '200ms' : '0ms' }}
        >
          <h3 className="text-2xl font-serif font-light text-[#7A3E53] mb-8">Reception Details</h3>
          <div className="space-y-6">
            <div>
              <p className="font-serif text-[#7A3E53] mb-3">Adults-Only Celebration</p>
              <p className="text-[#5A8E6E]">
                While we love your little ones, we kindly request an adults-only celebration at our reception. We can't wait to celebrate with you!
              </p>
            </div>
          </div>
        </div>
      </div>

      <div 
        ref={dressCodeRef}
        className={`max-w-2xl mx-auto mt-20 text-center transition-all duration-1000 ${
          dressCodeVisible 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
        }`}
      >
        <h3 className="text-2xl font-serif font-light text-[#7A3E53] mb-8">Dress Code</h3>
        <div className="p-8 border border-[#F7C843]/30 rounded-2xl bg-white/80 shadow-sm hover:shadow-lg transition-all duration-300">
          <p className="text-[#5A8E6E] text-lg font-semibold">
            Wear what makes you feel fabulous (bonus points for outshining us).
          </p>
        </div>
      </div>
    </div>
  );
};

export default EventDetailsSection;